import React, { useEffect, useState } from 'react';
import { TableCell } from 'semantic-ui-react';
import { Button, Colors, Display } from 'Views/Components/Button/Button';

interface ExpandableTableCellProps {
	content: string[] | null | undefined;
	maxItems?: number;
	fallback?: string;
	className?: string;
	separator?: string;
	singleColumn?: boolean;
	renderItem?: (item: string, index: number) => React.ReactNode;
}

export function ExpandableTableCell({
	content,
	maxItems = 3,
	fallback = '-',
	className = '',
	separator = ', ',
	singleColumn = false,
	renderItem,
}: ExpandableTableCellProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	// Reset expanded state when content changes (e.g. page change)
	useEffect(() => {
		setIsExpanded(false);
	}, [content?.join(',')]);

	// Handle null/undefined/empty content
	if (!content || content.length === 0) {
		return <TableCell className={className}>{fallback}</TableCell>;
	}

	// If content is short enough, just display it
	if (content.length <= maxItems) {
		return (
			<TableCell className={`expandable-table-cell ${className}`}>
				<div className="expandable-table-cell__content">
					<div
						className={`expandable-table-cell__items${singleColumn ? ' expandable-table-cell__items--single-column' : ''}`}
					>
						{renderItem ? content.map((item, index) => renderItem(item, index)) : content.join(separator)}
					</div>
				</div>
			</TableCell>
		);
	}

	// Content needs truncation
	const displayItems = isExpanded ? content : content.slice(0, maxItems);
	const remainingCount = content.length - maxItems;

	return (
		<TableCell className={`expandable-table-cell ${className}`}>
			<div className="expandable-table-cell__content">
				<div
					className={`expandable-table-cell__items${singleColumn ? ' expandable-table-cell__items--single-column' : ''}`}
				>
					{renderItem
						? displayItems.map((item, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<div key={index} className="expandable-table-cell__item">
									{renderItem(item, index)}
								</div>
							))
						: displayItems.map((item, index) => (
								// eslint-disable-next-line react/no-array-index-key
								<div key={index} className="expandable-table-cell__item">
									{item}
								</div>
							))}
				</div>
				<Button
					newStyles
					display={Display.Text}
					colors={Colors.Secondary}
					onClick={e => {
						e.stopPropagation();
						setIsExpanded(!isExpanded);
					}}
					className="expandable-table-cell__toggle"
				>
					{isExpanded ? 'Show less' : `Show ${remainingCount} more`}
				</Button>
			</div>
		</TableCell>
	);
}
